
import { useState, useEffect, useCallback } from 'react';
import { fetchFlightsData } from '../services/api';

// Helper functions for localStorage
const saveDateFilterToStorage = (filter, date, dateRange) => {
  try {
    localStorage.setItem('tripInfoDateFilter', JSON.stringify({
      filterId: filter,
      date: date ? date.toISOString() : null,
      dateRange: dateRange ? {
        start: dateRange.start ? dateRange.start.toISOString() : null,
        end: dateRange.end ? dateRange.end.toISOString() : null
      } : null
    }));
  } catch (err) {
    console.error('Error saving date filter to localStorage:', err);
  }
};

// const getDateFilterFromStorage = () => {
//   try {
//     const storedFilter = localStorage.getItem('tripInfoDateFilter');
//     if (!storedFilter) return null;

//     const parsed = JSON.parse(storedFilter);
//     return {
//       filterId: parsed.filterId,
//       date: parsed.date ? new Date(parsed.date) : null,
//       dateRange: parsed.dateRange ? {
//         start: parsed.dateRange.start ? new Date(parsed.dateRange.start) : null,
//         end: parsed.dateRange.end ? new Date(parsed.dateRange.end) : null
//       } : null
//     };
//   } catch (err) {
//     console.error('Error retrieving date filter from localStorage:', err);
//     return null;
//   }
// };

/**
 * Custom hook for managing trip information
 * @returns {Object} Object containing trip data and methods
 */
const useTripInfo = () => {
  const [tripData, setTripData] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Add caching to prevent duplicate API calls
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const [lastLoadParams, setLastLoadParams] = useState(null);
  const [pagination, setPagination] = useState({
    itemsPerPage: 25,
    pageNumber: 1,
    totalItems: 0,
    totalPages: 0
  });
  // Replace general search query with specific search fields
  const [userId, setUserId] = useState('');
  const [ticketId, setTicketId] = useState('');
  const [email, setEmail] = useState('');
  const [pnr, setPnr] = useState('');
  // Default departure date to today
  const [departureDate, setDepartureDate] = useState(new Date());
  const [ticketSource, setTicketSource] = useState('');
  // Add support for multiple ticket sources
  const [ticketSources, setTicketSources] = useState([]);
  const [belongsTo, setBelongsTo] = useState('all');
  // Add date range for departure date
  const [departureDateRange, setDepartureDateRange] = useState({ start: null, end: null });
  // Add separate state for departure date min/max for API compatibility
  const [departureDateMin, setDepartureDateMin] = useState('');
  const [departureDateMax, setDepartureDateMax] = useState('');
  const [sortBy, setSortBy] = useState('departureDate');
  const [sortDirection, setSortDirection] = useState('desc');
  // Add state for active quick filter
  const [activeDepartureDateQuickFilter, setActiveDepartureDateQuickFilter] = useState(null);

  // Add state to track if this is the initial load
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [usingFallback, setUsingFallback] = useState(false);

  // Fetch all trip data
  const loadTripData = useCallback(async (page = 1, itemsPerPage = 25) => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page: page,
        itemsPerPage: itemsPerPage
      };

      // Add search parameters if they exist
      if (userId) {
        params.userId = Number(userId);
      }
      if (ticketId) {
        params.ticketId = Number(ticketId);
      }
      if (email) {
        params.email = email;
      }
      if (pnr) {
        params.pnr = pnr;
      }
      // Handle departure date parameter for the API call
      // If departureDate is set, use it directly
      if (departureDate) {
        // Format date in YYYY-MM-DD format preserving the local date without timezone shift
        const year = departureDate.getFullYear();
        const month = String(departureDate.getMonth() + 1).padStart(2, '0');
        const day = String(departureDate.getDate()).padStart(2, '0');
        params.departureDate = `${year}-${month}-${day}`;
        console.log(`✅ Using departureDate for API call: ${params.departureDate}`);
      }
      // If no departureDate but we have a date range, use the start date
      else if (departureDateRange.start) {
        const startDate = departureDateRange.start;
        const startYear = startDate.getFullYear();
        const startMonth = String(startDate.getMonth() + 1).padStart(2, '0');
        const startDay = String(startDate.getDate()).padStart(2, '0');
        params.departureDate = `${startYear}-${startMonth}-${startDay}`;
        console.log(`Using departureDateRange.start as departureDate: ${params.departureDate}`);
      }
      // If neither is set, don't include a departureDate parameter
      // This allows the API to return data without date filtering
      else {
        console.log('❌ No departure date filter applied');
      }
      // Handle ticket source filtering - support both single and multiple sources
      if (ticketSources && ticketSources.length > 0) {
        // For multiple sources, send as comma-separated string
        params.ticketSource = ticketSources.join(',');

        // Also add individual source parameters as backup for API compatibility
        ticketSources.forEach((source, index) => {
          if (index > 0) {
            params[`ticketSource${index + 1}`] = source;
          }
        });
      } else if (ticketSource) {
        // Fallback to single source for backward compatibility
        params.ticketSource = ticketSource;
      }

      // Add belongsTo parameter if it's not 'all'
      if (belongsTo && belongsTo !== 'all') {
        params.belongsTo = belongsTo;
      }

      // Add departure date range parameters
      if (departureDateMin) {
        params.departureDateMin = departureDateMin;
      }
      if (departureDateMax) {
        params.departureDateMax = departureDateMax;
      }

      // Check if we're making the same request as before (after adding all params)
      const currentParams = JSON.stringify(params);
      if (hasInitiallyLoaded && lastLoadParams === currentParams) {
        console.log('Skipping duplicate API call for trip data');
        setLoading(false);
        return;
      }

      console.log('Loading trip data with params:', params);

      const data = await fetchFlightsData(params);

      // Cache the request parameters
      setLastLoadParams(currentParams);
      setHasInitiallyLoaded(true);

      // If no data is returned and we have multiple ticket sources, try individual calls as fallback
      if ((!data || data.length === 0) && ticketSources && ticketSources.length > 1) {
        setUsingFallback(true);
        let combinedData = [];

        for (const source of ticketSources) {
          try {
            // Create clean params for individual source call
            const individualParams = { ...params };

            // Remove all ticketSource-related parameters
            Object.keys(individualParams).forEach(key => {
              if (key.startsWith('ticketSource')) {
                delete individualParams[key];
              }
            });

            // Add single source parameter
            individualParams.ticketSource = source;

            const individualData = await fetchFlightsData(individualParams);
            if (individualData && individualData.length > 0) {
              combinedData = [...combinedData, ...individualData];
            }
          } catch (err) {
            console.warn(`Failed to fetch data for source ${source}:`, err);
          }
        }

        if (combinedData.length > 0) {
          // Remove duplicates based on userId and ticketId
          const uniqueData = combinedData.filter((item, index, self) =>
            index === self.findIndex(t => t.userId === item.userId && t.ticketId === item.ticketId)
          );
          setTripData(uniqueData);
        } else {
          setTripData(data || []);
        }
        setUsingFallback(false);
      } else {
        setUsingFallback(false);
        setTripData(data || []);
      }

      // Update pagination info
      setPagination(prev => ({
        ...prev,
        pageNumber: page,
        itemsPerPage: itemsPerPage
      }));

      // We don't need to handle empty data arrays here
      // The TablePagination component will continue to the next page
      // until the data array is empty
    } catch (err) {
      setError(err.message || 'Failed to load trip data');
      console.error('Error loading trip data:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, ticketId, email, pnr, departureDate, departureDateMin, departureDateMax, ticketSource, ticketSources, departureDateRange, belongsTo, hasInitiallyLoaded, lastLoadParams]);

  // Load trip data on mount without default date filter
  useEffect(() => {
    if (isInitialLoad) {
      // Clear any saved date filter from localStorage
      localStorage.removeItem('tripInfoDateFilter');

      // Don't set any default date filter - let users choose
      console.log('🚀 Loading initial trip data without date filter');
      setIsInitialLoad(false);

      // Load data after initial setup without date filter
      loadTripData(pagination.pageNumber, pagination.itemsPerPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialLoad]); // Intentionally excluding loadTripData and pagination to prevent infinite loops

  // Separate effect to handle pagination changes
  useEffect(() => {
    if (!isInitialLoad) {
      loadTripData(pagination.pageNumber, pagination.itemsPerPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageNumber, pagination.itemsPerPage]); // Intentionally excluding isInitialLoad and loadTripData to prevent infinite loops

  // Effect to handle automatic API calls when date filters change
  useEffect(() => {
    if (!isInitialLoad && hasInitiallyLoaded) {
      console.log('🗓️ Date filter changed, automatically loading trip data');
      console.log('Date filters:', { departureDate, departureDateMin, departureDateMax });

      // Reset to first page when filters change
      setPagination(prev => ({ ...prev, pageNumber: 1 }));
      loadTripData(1, pagination.itemsPerPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departureDate, departureDateMin, departureDateMax]); // Automatically trigger when any date filter changes

  // Search with current filters
  const searchWithFilters = useCallback(() => {
    setPagination(prev => ({ ...prev, pageNumber: 1 }));
    loadTripData(1, pagination.itemsPerPage);
  }, [loadTripData, pagination.itemsPerPage]);

  // Change page
  const changePage = useCallback((newPage) => {
    // Continue to increase page number until data array is empty
    setPagination(prev => ({
      ...prev,
      pageNumber: newPage
    }));
  }, []);

  // Handle sort
  const handleSort = useCallback((column) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  }, [sortBy, sortDirection]);

  // Filter and sort trip data
  // Since we're now filtering on the server side, we only need to sort the data
  const filteredTripData = tripData
    .sort((a, b) => {
      const direction = sortDirection === 'asc' ? 1 : -1;
      switch (sortBy) {
        case 'userId':
          return direction * (a.userId - b.userId);
        case 'ticketId':
          return direction * (a.ticketId - b.ticketId);
        case 'email':
          return direction * (a.email || '').localeCompare(b.email || '');
        case 'departureIata':
          return direction * (a.departureIata || '').localeCompare(b.departureIata || '');
        case 'arrivalIata':
          return direction * (a.arrivalIata || '').localeCompare(b.arrivalIata || '');
        case 'departureDate':
          return direction * (new Date(a.departureDate) - new Date(b.departureDate));
        case 'arrivalDate':
          return direction * (new Date(a.arrivalDate) - new Date(b.arrivalDate));
        case 'checkInStatus':
          return direction * (a.checkInStatus || 'NONE').localeCompare(b.checkInStatus || 'NONE');
        default:
          return direction * (new Date(a.departureDate) - new Date(b.departureDate));
      }
    });

  // Reset all filters
  const handleReset = useCallback(() => {
    setUserId('');
    setTicketId('');
    setEmail('');
    setPnr('');

    // Reset date filters to no selection (clear all date filters)
    setDepartureDate(null);
    setDepartureDateRange({ start: null, end: null });
    setDepartureDateMin('');
    setDepartureDateMax('');
    setActiveDepartureDateQuickFilter(null);

    // Clear saved filter from localStorage
    localStorage.removeItem('tripInfoDateFilter');

    setTicketSource('');
    setTicketSources([]);
    setBelongsTo('all');
    setSortBy('departureDate');
    setSortDirection('desc');

    // Reload data with reset filters (no date filter)
    loadTripData(1, pagination.itemsPerPage);
  }, [loadTripData, pagination.itemsPerPage]);

  // Custom setter for activeDepartureDateQuickFilter that also saves to localStorage
  const setActiveDepartureDateQuickFilterWithStorage = useCallback((filterId) => {
    setActiveDepartureDateQuickFilter(filterId);
    saveDateFilterToStorage(filterId, departureDate, departureDateRange);
  }, [departureDate, departureDateRange]);

  // Custom setter for departureDate that also updates localStorage
  const setDepartureDateWithStorage = useCallback((date) => {
    setDepartureDate(date);
    saveDateFilterToStorage(activeDepartureDateQuickFilter, date, departureDateRange);
  }, [activeDepartureDateQuickFilter, departureDateRange]);

  // Custom setter for departureDateRange that also updates localStorage
  const setDepartureDateRangeWithStorage = useCallback((dateRange) => {
    setDepartureDateRange(dateRange);
    saveDateFilterToStorage(activeDepartureDateQuickFilter, departureDate, dateRange);
  }, [activeDepartureDateQuickFilter, departureDate]);

  return {
    tripData: filteredTripData,
    allTripData: tripData,
    selectedTrip,
    loading,
    error,
    pagination,
    setPagination,
    changePage,
    loadTripData,
    searchWithFilters,
    setSelectedTrip,
    userId,
    setUserId,
    ticketId,
    setTicketId,
    email,
    setEmail,
    pnr,
    setPnr,
    departureDate,
    setDepartureDate: setDepartureDateWithStorage,
    departureDateRange,
    setDepartureDateRange: setDepartureDateRangeWithStorage,
    departureDateMin,
    setDepartureDateMin,
    departureDateMax,
    setDepartureDateMax,
    activeDepartureDateQuickFilter,
    setActiveDepartureDateQuickFilter: setActiveDepartureDateQuickFilterWithStorage,
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    handleSort,
    handleReset,
    ticketSource,
    setTicketSource,
    ticketSources,
    setTicketSources,
    belongsTo,
    setBelongsTo,
    usingFallback
  };
};

export default useTripInfo;
